import { useControllingJob } from "@vidario/api-client";
import {
  countLabel,
  ENTRY_NOUN,
  formatCostString,
  formatCount,
  type CostActivity,
  type CostPosten,
  type CostProvider,
  type JobCostSummary,
} from "@vidario/domain";
import { Spinner } from "@vidario/ui";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState, type ReactElement } from "react";

const PROVIDER_LABEL: Record<CostProvider, string> = {
  claude: "Claude",
  serpapi: "SerpAPI",
  brightdata: "BrightData",
};

const OPERATION_LABEL: Record<string, string> = {
  scoring: "Scoring",
  finding: "Suche",
  enriching: "Anreicherung",
  expansion: "Query-Expansion",
  job_parse: "Job-Parsing",
};

function operationLabel(operation: string): string {
  return OPERATION_LABEL[operation] ?? operation;
}

/** One expandable job in the Controlling table. The per-job breakdown is
 * fetched lazily — `useControllingJob` only fires once the row is opened,
 * so the overview stays cheap and detail loads on demand. */
export function JobCostRow({ summary }: { summary: JobCostSummary }): ReactElement {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useControllingJob(summary.jobId, {
    enabled: open,
  });

  return (
    <div className="border-b border-border-default last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
      >
        <span aria-hidden="true" className="text-text-tertiary">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
        <span className="flex-1 font-medium">{summary.jobTitle}</span>
        <span className="text-xs text-text-tertiary">
          {countLabel(summary.entries, ENTRY_NOUN)}
        </span>
        <span className="tabular-nums w-28 text-right font-semibold">
          {formatCostString(summary.total, summary.currency)}
        </span>
      </button>

      {open && (
        <div className="border-t border-border-default bg-neutral-50 px-5 py-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <Spinner size={16} />
              <span>Aufschlüsselung wird geladen…</span>
            </div>
          )}
          {error && (
            <p className="text-sm text-danger-600" role="alert">
              Aufschlüsselung konnte nicht geladen werden.
            </p>
          )}
          {data && data.activities.length === 0 && (
            <p className="text-sm text-text-tertiary">Keine Aktivitäten erfasst.</p>
          )}
          {data?.activities.map((activity, i) => (
            <ActivityBlock
              key={`${activity.type}-${activity.runId ?? "none"}-${i}`}
              activity={activity}
              currency={data.currency}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  completed: "Abgeschlossen",
  running: "Läuft",
  failed: "Fehlgeschlagen",
  pending: "Wartend",
};

function ActivityBlock({
  activity,
  currency,
}: {
  activity: CostActivity;
  currency: string;
}): ReactElement {
  return (
    <div className="mb-3 overflow-hidden rounded-md border border-border-default bg-neutral-0 last:mb-0">
      <div className="flex items-center gap-3 border-b border-border-default px-4 py-2.5">
        <span className="flex-1 text-sm font-medium">{activity.label}</span>
        {activity.status && (
          <span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700">
            {STATUS_LABEL[activity.status] ?? activity.status}
          </span>
        )}
        <span className="tabular-nums w-24 text-right text-sm font-semibold">
          {formatCostString(activity.amount, currency)}
        </span>
      </div>
      {/* Narrow screens scroll the table sideways inside the border. */}
      <div
        role="region"
        aria-label={`Posten: ${activity.label}`}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scroll area with nothing focusable inside must take focus itself, or the keyboard can't scroll it
        tabIndex={0}
        className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
      >
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left">
              <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                Posten
              </th>
              <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                Verbrauch
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                Kosten
              </th>
            </tr>
          </thead>
          <tbody>
            {activity.postens.map((posten, i) => (
              <PostenRow
                key={`${posten.provider}-${posten.operation}-${i}`}
                posten={posten}
                currency={currency}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PostenRow({ posten, currency }: { posten: CostPosten; currency: string }): ReactElement {
  const usage =
    posten.provider === "claude"
      ? `${formatCount(posten.inputTokens)} / ${formatCount(posten.outputTokens)} Tokens`
      : `${formatCount(posten.units)} Calls`;

  return (
    <tr className="border-t border-border-default">
      <td className="px-4 py-2">
        <span className="font-medium">{PROVIDER_LABEL[posten.provider]}</span>
        <span className="text-text-tertiary">
          {" · "}
          {operationLabel(posten.operation)}
        </span>
      </td>
      <td className="tabular-nums px-4 py-2 text-text-secondary">{usage}</td>
      <td className="tabular-nums px-4 py-2 text-right">
        {formatCostString(posten.amount, currency)}
      </td>
    </tr>
  );
}
